import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getBranches, deleteBranch } from "@/integrations/supabase/services";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Plus, Edit, Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Branch } from "@/types/models";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import BranchForm from "@/components/branches/BranchForm";
import { useToast } from "@/hooks/use-toast";

type BrachesData = Branch[];

const Branches = () => {
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: branches, isLoading } = useQuery<BrachesData>({
    queryKey: ["branches"],
    queryFn: getBranches,
  });

  // Delete Branch mutation
  const deleteBranchMutation = useMutation({
    mutationFn: deleteBranch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast({ title: "Sucursal eliminada correctamente" });
      setIsConfirmDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ 
        variant: "destructive", 
        title: "Error al eliminar la sucursal", 
        description: error.message 
      });
    },
  });

  const handleAddBranch = () => {
    setSelectedBranch(null);
    setIsDialogOpen(true);
  };

  const handleEditBrach = (branch: Branch) => {
    setSelectedBranch(branch);
    setIsDialogOpen(true);
  };

  const handleDeleteBrach = (branch: Branch) => {
    setSelectedBranch(branch);
    setIsConfirmDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = () => {
    if (selectedBranch) {
      deleteBranchMutation.mutate(selectedBranch.id);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Sucursales</h1>
          <Button onClick={handleAddBranch} className="flex items-center gap-2">
            <Plus size={16} />
            Nueva Sucursal
          </Button>
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Dirección</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Porc. Operador</TableHead>
                <TableHead>Porc. Administrador</TableHead>
                <TableHead className="w-[100px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    Cargando sucursales...
                  </TableCell>
                </TableRow>
              ) : branches.length === 0 ? (
              <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    No hay sucursales registradas
                  </TableCell>
                </TableRow>
              ) : (
                branches.map((branch) => (
                  <TableRow key={branch.id}>
                    <TableCell className="font-medium py-2">{branch.name}</TableCell>
                    <TableCell className="py-2">{branch.address}</TableCell>
                    <TableCell className="py-2">{branch.phone}</TableCell>
                    <TableCell className="py-2">
                      {branch.operator_percentage !== null && branch.operator_percentage !== undefined
                        ? `${branch.operator_percentage}%`
                        : "Por Defecto 25%"}
                    </TableCell>
                    <TableCell className="py-2">
                      {branch.administrator_percentage !== null && branch.administrator_percentage !== undefined
                        ? `${branch.administrator_percentage}%`
                        : "Por Defecto 75%"}
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleEditBrach(branch)}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteBrach(branch)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Edit Branch Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedBranch ? "Editar Sucursal" : "Nueva Sucursal"}
            </DialogTitle>
            <DialogDescription>
              Actualice la información de la sucursal.
            </DialogDescription>
          </DialogHeader>
          <BranchForm 
            branch={selectedBranch} 
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['branches'] });
              toast({ title: selectedBranch ? "Sucursal actualizada correctamente" : "Sucursal creada correctamente" });
              setIsDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>¿Estás seguro que deseas eliminar esta sucursal?</p>
            <p className="font-medium">{selectedBranch?.name}</p>
          </div>
          <DialogFooter className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsConfirmDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteBranchMutation.isPending}
            >
              {deleteBranchMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Branches;
